import type { MessageThreadDto, PostMessageInput, ThreadSummaryDto } from "@tc/shared";
import { ApiError } from "@/features/trips/api";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { accept: "application/json", "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message)
        message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new ApiError(message, res.status);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/**
 * Trip messaging calls (TW-3453) — the per-trip conversation. Co-edited: both the club and back
 * office read + post to the same RLS-scoped thread. The thread is created lazily server-side, so a
 * GET on a never-opened trip returns an empty thread rather than 404. `read` is the explicit
 * acknowledgement that advances the caller's last-read pointer (clearing their unread count).
 */
/** A trailing `?clubId=…` for back-office calls; the server ignores it for club users. */
function clubQuery(clubId?: string | null): string {
  return clubId ? `?clubId=${encodeURIComponent(clubId)}` : "";
}

export const messagingApi = {
  // Trip threads (TW-3453).
  getTripThread: (tripId: string) => req<MessageThreadDto>(`/trips/${tripId}/messages`),
  postTrip: (tripId: string, body: PostMessageInput) =>
    req<MessageThreadDto>(`/trips/${tripId}/messages`, { method: "POST", body: JSON.stringify(body) }),
  markTripRead: (tripId: string) =>
    req<{ readAt: string }>(`/trips/${tripId}/messages/read`, { method: "POST" }),

  // Inbox + general (non-trip) thread (TW-3454).
  inbox: (clubId?: string | null) => req<ThreadSummaryDto[]>(`/messages/threads${clubQuery(clubId)}`),
  getGeneral: (clubId?: string | null) => req<MessageThreadDto>(`/messages/general${clubQuery(clubId)}`),
  postGeneral: (body: PostMessageInput, clubId?: string | null) =>
    req<MessageThreadDto>(`/messages/general${clubQuery(clubId)}`, { method: "POST", body: JSON.stringify(body) }),
  markGeneralRead: (clubId?: string | null) =>
    req<{ readAt: string }>(`/messages/general/read${clubQuery(clubId)}`, { method: "POST" }),
};
